'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import {
  initAnalytics,
  trackPageView,
  identifyUser,
  resetUser
} from '@/lib/analytics'
import { useAuth } from '@/hooks/useAuth'

/**
 * Analytics provider component that:
 * 1. Initializes analytics providers on mount
 * 2. Tracks page views on route changes
 * 3. Identifies/resets user on auth state changes
 */
export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const { user } = useAuth()
  const previousUserIdRef = useRef<string | null>(null)
  const isInitializedRef = useRef(false)

  // Initialize analytics on mount
  useEffect(() => {
    if (!isInitializedRef.current) {
      initAnalytics()
      isInitializedRef.current = true
    }
  }, [])

  // Track page views on route changes
  useEffect(() => {
    if (!isInitializedRef.current) return
    trackPageView(pathname)
  }, [pathname])

  // Handle user identification
  useEffect(() => {
    if (!isInitializedRef.current) return

    const currentUserId = user?.id || null

    // User logged in
    if (currentUserId && currentUserId !== previousUserIdRef.current) {
      identifyUser(currentUserId, {
        email: user?.email,
        created_at: user?.created_at
      })
    }

    // User logged out
    if (!currentUserId && previousUserIdRef.current) {
      resetUser()
    }

    previousUserIdRef.current = currentUserId
  }, [user])

  return <>{children}</>
}
